import {
  Component,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
  OnInit,
} from '@angular/core';
import { BuyCoinsComponent } from './buy-coins/buy-coins.component.js';
import {
  CoinflowCardMetadata,
  PurchaseView,
  PurchasePaymentMethod,
  PurchaseSelection,
} from '../common.js';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CryptoPurchaseComponent } from './crypto-purchase/crypto-purchase.component.js';
import { toSignal } from '@angular/core/rxjs-interop';
import { PurchasePaymentMethodComponent } from './purchase-payment-method/purchase-payment-method.component.js';
import { CardConfirmPurchaseComponent } from './card-confirm-purchase/card-confirm-purchase.component.js';
import { CryptoConfirmPurchaseComponent } from './crypto-confirm-purchase/crypto-confirm-purchase.component.js';
import { CardPurchaseComponent } from './card-purchase/card-purchase.component.js';
import { KycPurchaseComponent } from './kyc/kyc-purchase.component.js';
import { PaymentsOfflineComponent } from './payments-offline/payments-offline.component.js';
import { UserViewService } from '../../../core/view-states/user-view.service.js';
import { LOGGER_FACTORY } from '../../../core/logger/logger-factory.provider.js';
import { CasinoSettingKeyId, PaymentMethod } from '@gains-com/shared';
import { MetadataViewService } from '../../../core/view-states/metadata-view.service.js';
import { ActivePurchaseViewService } from '../../../core/view-states/active-purchase-view.service.js';
import { AccountLimitsViewService } from '../../../core/view-states/account-limits-view.service.js';

export interface PurchasesDialogData {
  noSCInitialView?: boolean;
  /** Where the dialog was opened from. 'wallet' shows a back arrow on the
   * payments-offline view that re-opens the wallet dialog; 'direct' (default)
   * hides the back arrow because there's no upstream view to return to. */
  source?: 'wallet' | 'direct';
}

@Component({
  selector: 'app-purchases-dialog',
  templateUrl: './purchases-dialog.component.html',
  styleUrls: ['./purchases-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BuyCoinsComponent,
    CryptoPurchaseComponent,
    PurchasePaymentMethodComponent,
    CardConfirmPurchaseComponent,
    CryptoConfirmPurchaseComponent,
    CardPurchaseComponent,
    KycPurchaseComponent,
    PaymentsOfflineComponent,
  ],
})
export class PurchasesDialogComponent implements OnInit {
  // Service Injections
  readonly data: PurchasesDialogData = inject(DIALOG_DATA) ?? {};
  private readonly _userView = inject(UserViewService);
  private readonly _dialogRef = inject(DialogRef);
  private readonly _metadataView = inject(MetadataViewService);
  private readonly _logger = inject(LOGGER_FACTORY).create('PurchasesDialog');
  private readonly _activePurchaseViewService = inject(
    ActivePurchaseViewService
  );
  private readonly _accountLimitsView = inject(AccountLimitsViewService);

  // Observable to Signal Conversions
  fiatPurchaseEnabledPlatform = toSignal(
    this._metadataView.casinoSettingValue$<boolean>(
      CasinoSettingKeyId.PurchasesFiat
    )
  );
  cryptoPurchaseEnabledPlatform = toSignal(
    this._metadataView.casinoSettingValue$<boolean>(
      CasinoSettingKeyId.PurchasesCrypto
    )
  );
  userState = toSignal(this._userView.state$);
  purchase = toSignal(this._activePurchaseViewService.activePurchase$);

  // Computed Signals
  fiatPurchaseEnabledUser = computed(
    () =>
      (this.userState()?.purchases_fiat_enabled &&
        this.userState()?.country_enabled) ??
      false
  );
  fiatPurchaseEnabled = computed(
    () => this.fiatPurchaseEnabledPlatform() && this.fiatPurchaseEnabledUser()
  );
  cryptoPurchaseEnabledUser = computed(
    () => this.userState()?.purchases_crypto_enabled ?? false
  );
  cryptoPurchaseEnabled = computed(
    () =>
      this.cryptoPurchaseEnabledPlatform() && this.cryptoPurchaseEnabledUser()
  );

  // toSignal returns undefined until the first emission. Without this gate,
  // bothDisabled would flip true on first render (since !undefined === true)
  // and flash the offline screen before the real flags arrive.
  private _flagsLoaded = computed(
    () =>
      this.fiatPurchaseEnabledPlatform() !== undefined &&
      this.cryptoPurchaseEnabledPlatform() !== undefined &&
      this.userState() !== undefined
  );

  bothDisabled = computed(
    () =>
      this._flagsLoaded() &&
      !this.fiatPurchaseEnabled() &&
      !this.cryptoPurchaseEnabled()
  );

  // Sitewide if both casino-setting flags are off — the user's per-account flags
  // don't matter when the platform itself has paused payments.
  // Otherwise (at least one platform setting is on but the user is locked out)
  // → user-level cause.
  disabledCause = computed<'sitewide' | 'user'>(() =>
    !this.fiatPurchaseEnabledPlatform() && !this.cryptoPurchaseEnabledPlatform()
      ? 'sitewide'
      : 'user'
  );

  // Reactive Signals
  view = signal<PurchaseView>('buy-coins');
  kycContinueView = signal<PurchaseView>('buy-coins');
  purchaseSelection = signal<PurchaseSelection | null>(null);
  selectedPaymentMethod = signal<PurchasePaymentMethod | null>(null);
  selectedSavedCard = signal<PaymentMethod<CoinflowCardMetadata> | null>(null);
  purchaseLimitReached = signal(false);

  close(result?: 'contact-support' | void) {
    this._activePurchaseViewService.clearActivePurchasePayment();
    this._dialogRef.close(result || undefined);
  }

  /**
   * Close the purchases dialog and signal the caller to re-open the wallet
   * dialog. Used by the payments-offline back arrow when the user came from
   * the wallet. The active-purchase clear runs in WalletDialogService after
   * getDialogResult resolves, so it's not needed here.
   */
  backToWallet() {
    this._dialogRef.close('reopen-wallet');
  }

  // The cap was hit since `/self` last loaded (another device, or the
  // realtime refetch has not landed yet), so the copy is shown from this
  // flag rather than waiting on the derived state; the refetch then locks
  // the entry points outside this dialog as well.
  handlePurchaseLimitReached() {
    this.purchaseLimitReached.set(true);
    this.view.set('buy-coins');
    this._userView.syncPurchaseCap().catch(() => {});
  }

  forceKyc(continueView: PurchaseView) {
    this.kycContinueView.set(continueView);
    this.view.set('kyc');
  }

  selectPurchase({
    selection,
    forceKyc,
  }: {
    selection: PurchaseSelection;
    forceKyc?: boolean;
  }) {
    this.purchaseSelection.set(selection);

    if (forceKyc) {
      this.forceKyc('purchase-payment-method');
    } else {
      this.view.set('purchase-payment-method');
    }
  }

  /**
   * Combined handler: sets the purchase selection AND navigates directly
   * to the appropriate payment view, skipping the payment-method selection step.
   */
  selectPurchaseWithMethod({
    selection,
    paymentMethod,
    forceKyc,
    savedCard,
  }: {
    selection: PurchaseSelection;
    paymentMethod: PurchasePaymentMethod;
    forceKyc?: boolean;
    savedCard?: PaymentMethod<CoinflowCardMetadata>;
  }) {
    this.purchaseSelection.set(selection);
    this.selectedPaymentMethod.set(paymentMethod);
    this.selectedSavedCard.set(savedCard ?? null);

    const targetView = this._resolvePaymentView(paymentMethod);

    if (forceKyc) {
      this.forceKyc(targetView);
    } else {
      this.view.set(targetView);
    }
  }

  setPaymentMethod(paymentMethod: PurchasePaymentMethod) {
    this.view.set(this._resolvePaymentView(paymentMethod));
  }

  private _resolvePaymentView(
    paymentMethod: PurchasePaymentMethod
  ): PurchaseView {
    switch (paymentMethod) {
      case 'card':
        return 'card-purchase';
      case 'crypto':
        return 'crypto-purchase';
      case 'apple-pay':
      case 'google-pay':
        return 'purchase-payment-method';
      default:
        return 'card-purchase';
    }
  }

  handleKycContinue() {
    this.view.set(this.kycContinueView());
  }

  ngOnInit() {
    // Non-blocking: stale-while-revalidate for non-KYC users
    if (!this.userState()?.kyc_verified) {
      this._accountLimitsView.fetchLifetimePurchaseLimits().catch((error) => {
        this._logger.error('Failed to fetch lifetime purchase limits', {
          error,
        });
      });
    }

    // The Coinflow purchase-method reconcile now runs (and gates the saved-card
    // picker) inside BuyCoinsComponent. It's no longer fired here to avoid a
    // duplicate POST.
  }
}
